import React, { useEffect } from 'react';
import {
    Box,
    Drawer,
    Button,
    Stack,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    DialogTitle,
} from '@mui/material';
import { useFormik } from 'formik';
import { useSearchParams } from 'react-router-dom';
import { Close } from '@mui/icons-material';
import DocumentInputMasked from '../../../../components/DocumentInputMasked';
import { BRAZIL_UF_OPTIONS, brazilUfValue } from '../../../../constants/brazilUf';

export const BRANCH_FILTER_KEYS = [
    'name',
    'businessName',
    'document',
    'status',
    'state',
    'city',
    'responsibleName',
    'responsibleEmail',
];

function filtersFromParams(searchParams) {
    return {
        name: searchParams.get('name') || '',
        businessName: searchParams.get('businessName') || '',
        document: searchParams.get('document') || '',
        status: searchParams.get('status') || '',
        state: brazilUfValue(searchParams.get('state')) || searchParams.get('state') || '',
        city: searchParams.get('city') || '',
        responsibleName: searchParams.get('responsibleName') || '',
        responsibleEmail: searchParams.get('responsibleEmail') || '',
    };
}

export function SearchDrawerBranches({ open, onClose, onClearFilters }) {
    const [searchParams, setSearchParams] = useSearchParams();

    const { values, handleSubmit, setFieldValue, handleChange, resetForm } = useFormik({
        initialValues: filtersFromParams(searchParams),
        onSubmit: formValues => {
            const next = {};
            const perPage = searchParams.get('perPage');
            if (perPage) next.perPage = perPage;

            Object.entries(formValues).forEach(([key, value]) => {
                if (value !== '' && value != null) next[key] = value;
            });

            setSearchParams(next);
            onClose();
        },
    });

    useEffect(() => {
        resetForm({ values: filtersFromParams(searchParams) });
    }, [searchParams, resetForm]);

    const handleClearFilters = () => {
        onClearFilters();
        setSearchParams({});
        resetForm({ values: filtersFromParams(new URLSearchParams()) });
    };

    const hasFilters = Object.values(values).some(value => value !== '' && value != null);

    return (
        <Drawer anchor="right" open={open} onClose={onClose}>
            <Box sx={{ padding: 2, width: 350 }}>
                <DialogTitle sx={{ padding: 0 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                        Buscar filiais
                        <IconButton size="small" onClick={onClose}>
                            <Close />
                        </IconButton>
                    </Stack>
                </DialogTitle>
                <form onSubmit={handleSubmit}>
                    <Stack spacing={2}>
                        <TextField
                            fullWidth
                            label="Nome da filial"
                            variant="outlined"
                            name="name"
                            value={values.name}
                            onChange={handleChange}
                        />
                        <TextField
                            label="Razão social"
                            variant="outlined"
                            fullWidth
                            name="businessName"
                            value={values.businessName}
                            onChange={handleChange}
                        />
                        <FormControl fullWidth>
                            <InputLabel id="branch-status-label">Status</InputLabel>
                            <Select
                                labelId="branch-status-label"
                                id="status"
                                name="status"
                                value={values.status}
                                onChange={handleChange}
                                label="Status"
                            >
                                <MenuItem value="">Todos</MenuItem>
                                <MenuItem value="true">Ativo</MenuItem>
                                <MenuItem value="false">Inativo</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            fullWidth
                            label="Documento"
                            variant="outlined"
                            name="document"
                            value={values.document}
                            onChange={handleChange}
                            InputProps={{
                                inputComponent: DocumentInputMasked,
                            }}
                        />
                        <FormControl fullWidth>
                            <InputLabel id="branch-state-label">Estado</InputLabel>
                            <Select
                                labelId="branch-state-label"
                                id="state"
                                name="state"
                                value={brazilUfValue(values.state) || values.state || ''}
                                onChange={handleChange}
                                label="Estado"
                            >
                                <MenuItem value="">Todos</MenuItem>
                                {BRAZIL_UF_OPTIONS.map(option => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Cidade"
                            variant="outlined"
                            fullWidth
                            name="city"
                            value={values.city}
                            onChange={handleChange}
                        />
                        <TextField
                            label="Nome do responsável"
                            variant="outlined"
                            fullWidth
                            name="responsibleName"
                            value={values.responsibleName}
                            onChange={handleChange}
                        />
                        <TextField
                            label="E-mail do responsável"
                            variant="outlined"
                            fullWidth
                            name="responsibleEmail"
                            value={values.responsibleEmail}
                            onChange={handleChange}
                        />
                    </Stack>
                    <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                        <Button variant="contained" type="submit">
                            Buscar
                        </Button>
                        {hasFilters && (
                            <Button variant="outlined" onClick={handleClearFilters}>
                                Limpar filtros
                            </Button>
                        )}
                    </Stack>
                </form>
            </Box>
        </Drawer>
    );
}
